import {
  type DOMNode,
  type Element,
  htmlToDOM,
  type Text,
} from 'html-react-parser';

const ALLOWED_TAGS = new Set([
  'a',
  'address',
  'b',
  'br',
  'div',
  'em',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'li',
  'ol',
  'p',
  's',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
]);

const VOID_TAGS = new Set(['br', 'hr']);
const DROP_CONTENT_TAGS = new Set([
  'base',
  'embed',
  'form',
  'iframe',
  'link',
  'math',
  'meta',
  'noscript',
  'object',
  'script',
  'style',
  'svg',
  'template',
]);

const GLOBAL_ATTRIBUTES = new Set(['class', 'title']);
const TAG_ATTRIBUTES: Record<string, Set<string>> = {
  a: new Set(['href', 'rel', 'target']),
  ol: new Set(['start', 'type']),
  td: new Set(['colspan', 'rowspan']),
  th: new Set(['colspan', 'rowspan', 'scope']),
};

const SAFE_URL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);
const SAFE_TARGETS = new Set(['_blank', '_self', '_parent', '_top']);
const SAFE_TABLE_SCOPES = new Set(['col', 'row', 'colgroup', 'rowgroup']);
const INTEGER_ATTRIBUTES = new Set(['colspan', 'rowspan', 'start']);
const SAFE_CLASS_TOKEN_PATTERN = /^[a-zA-Z0-9_:/.-]+$/;

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const escapeAttribute = (value: string) =>
  escapeHtml(value).replace(/`/g, '&#96;');

const getNodeType = (node: DOMNode) => String(node.type);

const isTextNode = (node: DOMNode): node is Text =>
  getNodeType(node) === 'text';

const isElementNode = (node: DOMNode): node is Element =>
  'name' in node && typeof node.name === 'string' && 'attribs' in node;

const isAllowedAttribute = (tagName: string, attributeName: string) => {
  if (GLOBAL_ATTRIBUTES.has(attributeName)) return true;
  if (attributeName.startsWith('aria-') || attributeName.startsWith('data-'))
    return true;

  return TAG_ATTRIBUTES[tagName]?.has(attributeName) ?? false;
};

const sanitizeClassName = (value: string) =>
  value
    .split(/\s+/)
    .filter((token) => SAFE_CLASS_TOKEN_PATTERN.test(token))
    .join(' ');

const sanitizeIntegerAttribute = (value: string) => {
  const normalized = value.trim();
  return /^\d{1,3}$/.test(normalized) ? normalized : '';
};

const sanitizeAnchorTarget = (value: string) => {
  const normalized = value.trim().toLowerCase();
  return SAFE_TARGETS.has(normalized) ? normalized : '';
};

const isSafeUrl = (value: string) => {
  const normalized = value.trim();
  if (!normalized) return false;

  if (
    normalized.startsWith('#') ||
    normalized.startsWith('/') ||
    normalized.startsWith('./') ||
    normalized.startsWith('../')
  ) {
    return true;
  }

  try {
    const parsed = new URL(normalized);
    return SAFE_URL_PROTOCOLS.has(parsed.protocol);
  } catch {
    return false;
  }
};

const sanitizeRel = (value: string) =>
  Array.from(
    new Set(
      value
        .split(/\s+/)
        .map((token) => token.trim().toLowerCase())
        .filter(Boolean)
    )
  ).join(' ');

const sanitizeAttributeValue = (
  tagName: string,
  attributeName: string,
  attributeValue: string
): string => {
  if (attributeName === 'class') return sanitizeClassName(attributeValue);
  if (attributeName === 'href')
    return isSafeUrl(attributeValue) ? attributeValue.trim() : '';
  if (attributeName === 'target') return sanitizeAnchorTarget(attributeValue);
  if (attributeName === 'rel') return sanitizeRel(attributeValue);
  if (INTEGER_ATTRIBUTES.has(attributeName)) {
    return sanitizeIntegerAttribute(attributeValue);
  }
  if (tagName === 'ol' && attributeName === 'type') {
    return /^[1AaIi]$/.test(attributeValue.trim()) ? attributeValue.trim() : '';
  }
  if (attributeName === 'scope') {
    const normalized = attributeValue.trim().toLowerCase();
    return SAFE_TABLE_SCOPES.has(normalized) ? normalized : '';
  }

  return attributeValue.trim();
};

const serializeAttributes = (
  tagName: string,
  attributes: Element['attribs']
) => {
  const safeAttributes: Record<string, string> = {};

  Object.entries(attributes).forEach(([rawName, rawValue]) => {
    const attributeName = rawName.toLowerCase();
    if (attributeName.startsWith('on') || attributeName === 'style') return;
    if (!isAllowedAttribute(tagName, attributeName)) return;

    const safeValue = sanitizeAttributeValue(tagName, attributeName, rawValue);
    if (!safeValue) return;

    safeAttributes[attributeName] = safeValue;
  });

  if (tagName === 'a' && safeAttributes.target === '_blank') {
    safeAttributes.rel = sanitizeRel(
      `${safeAttributes.rel ?? ''} noopener noreferrer`
    );
  }

  return Object.entries(safeAttributes)
    .map(([name, value]) => ` ${name}="${escapeAttribute(value)}"`)
    .join('');
};

const serializeNodes = (nodes: DOMNode[]): string =>
  nodes.map((node) => serializeNode(node)).join('');

const serializeNode = (node: DOMNode): string => {
  if (isTextNode(node)) return escapeHtml(node.data);
  if (!isElementNode(node)) return '';

  const tagName = node.name.toLowerCase();
  if (DROP_CONTENT_TAGS.has(tagName)) return '';

  const children = serializeNodes(node.children as DOMNode[]);
  if (!ALLOWED_TAGS.has(tagName)) return children;

  const attributes = serializeAttributes(tagName, node.attribs);
  if (VOID_TAGS.has(tagName)) return `<${tagName}${attributes}>`;

  return `<${tagName}${attributes}>${children}</${tagName}>`;
};

/**
 * Sanitizes HTML using an allowlist of supported tags and attributes. This is
 * the security-only layer; CMS cleanup should happen before calling it.
 *
 * ```ts
 * import { sanitizeHtml } from '@/utils/format/html';
 *
 * sanitizeHtml('<p onclick="alert(1)">สมัคร <strong>วันนี้</strong></p>');
 * // '<p>สมัคร <strong>วันนี้</strong></p>'
 *
 * sanitizeHtml('<a href="javascript:alert(1)">ดาวน์โหลด</a>');
 * // '<a>ดาวน์โหลด</a>'
 * ```
 */
export const sanitizeHtml = (html: string): string => {
  if (!html) return '';

  try {
    return serializeNodes(htmlToDOM(html));
  } catch {
    return escapeHtml(html);
  }
};
